import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import IRIBufferingForm from 'routes/IRIBuffering/components/IRIBufferingForm';
import { Ref } from 'routes/IRIBuffering/components/IRIBufferingForm/IRIBufferingForm';
import { RootState } from 'data/root.reducers';
import * as selectors from 'data/iriBuffering/iriBuffering.selectors';
import * as actions from 'data/iriBuffering/iriBuffering.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { IRIBuffering } from 'data/iriBuffering/iriBuffering.types';
import { iriBufferingTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/IRIBuffering.adapter';
import { getAuditDetailsToSubmit } from '../../utils/iriBufferingDataToSubmit';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  iriBufferingTemplateDef: adaptTemplateVisibilityMode(iriBufferingTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  selectedIRIBuffering: selectors.getSelectedIRIBuffering(state)[0],
  iriBuffering: selectors.getIRIBuffering(state),
  originalSelectedIRIBuffering: selectors.getSelectedIRIBuffering(state)[0],
});

const mapDispatch = {
  updateIRIBuffering: actions.updateIRIBuffering,
  goIRIBufferingView: actions.goIRIBufferingView,
  getIRIBuffering: actions.getIRIBuffering,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const IRIBufferingEdit = ({
  history,
  originalSelectedIRIBuffering,
  iriBuffering,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  iriBufferingTemplateDef,
  updateIRIBuffering,
  goIRIBufferingView,
  getIRIBuffering,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<IRIBuffering>>({});
  const [template, setTemplate] = useState(iriBufferingTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getIRIBuffering(deliveryFunctionId);
  }, [deliveryFunctionId, getIRIBuffering]);

  useEffect(() => {
    if (iriBuffering != null && template != null) {
      const clonedTemplate = cloneDeep(template);
      setFormData(iriBuffering);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate.key = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }, [
    iriBuffering,
  ]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const iriBufferingData = formRef.current.getValues();

    if (iriBuffering) {
      iriBufferingData.CAUDPERIOD = iriBuffering.CAUDPERIOD;
      iriBufferingData.CAUDTIME = iriBuffering.CAUDTIME;
    }

    iriBufferingData.IRICLEAR = iriBufferingData.IRICLEAR ? 'Y' : 'N';

    const payload = {
      data: iriBufferingData,
      auditDetails: getAuditDetailsToSubmit(
        iriBufferingData,
        originalSelectedIRIBuffering,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload) {
      updateIRIBuffering(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goIRIBufferingView(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as IRIBuffering);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <IRIBufferingForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };
  if (iriBuffering == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="IRIBufferingEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('iriBuffering.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(IRIBufferingEdit));
